import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { italianName, localizedName } from "@/i18n/content";
import SeoAlternates from "@/components/SeoAlternates";
import { useMaterials, useApplications } from "@/hooks/useBioNexusData";
import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Reveal, { SplitWords } from "@/components/Reveal";
import CircularityLoop from "@/components/home/CircularityLoop";
import MaterialCard from "@/components/cards/MaterialCard";
import ApplicationCard from "@/components/cards/ApplicationCard";
import { SectionHeading } from "@/components/detail/DetailLayout";
import { useInView } from "@/hooks/useInView";

const ViewAll = ({ to }: { to: string }) => {
  const { m } = useI18n();
  return (
    <Link to={to} className="group shrink-0 text-sm font-medium text-primary">
      {m.common.viewAll} <span className="arrow-nudge">→</span>
    </Link>
  );
};

const CardSkeletons = ({ ratio }: { ratio: string }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-xl border border-border overflow-hidden">
        <div className={`${ratio} bg-muted animate-pulse`} />
        <div className="p-6 space-y-3">
          <div className="h-3 w-1/3 bg-muted animate-pulse rounded" />
          <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
        </div>
      </div>
    ))}
  </div>
);

/** Counts up to a number once it scrolls into view. */
const CountUp = ({ value }: { value: number }) => {
  const { ref, inView } = useInView<HTMLSpanElement>();
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(value);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 1200);
      setShown(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return <span ref={ref}>{shown}</span>;
};

const Home = () => {
  const { m, locale } = useI18n();
  const { data: materials, isLoading: materialsLoading } = useMaterials();
  const { data: applications, isLoading: applicationsLoading } = useApplications();

  const featuredMaterials = materials?.slice(0, 3) || [];
  const latestApplications = applications?.slice(0, 3) || [];

  // Distinct publications / project pages referenced by entries
  const sourceCount = useMemo(() => {
    const keys = new Set<string>();
    materials?.forEach((m) => {
      const key = m.source_doi || m.source_url || m.source_paper;
      if (key) keys.add(key.toLowerCase());
    });
    applications?.forEach((a) => {
      if (a.source_url) keys.add(a.source_url.toLowerCase());
    });
    return keys.size;
  }, [materials, applications]);

  // One hand-picked entry per life-cycle stage (real source photos only).
  // Matched by name so each stage always shows research that fits it; a stage
  // without a match shows no photo rather than an unrelated one.
  const loopImages = useMemo(() => {
    type Entry = { id: string; name: string; name_cn?: string | null; name_it: string | null; image_url: string | null; kind: "materials" | "applications" };
    const entries: Entry[] = [
      ...(applications || []).map((a) => ({
        id: a.id,
        name: a.title,
        name_cn: a.title_cn,
        name_it: italianName(a, "title"),
        image_url: a.image_url,
        kind: "applications" as const,
      })),
      ...(materials || []).map((mat) => ({
        id: mat.id,
        name: mat.name,
        name_cn: mat.name_cn,
        name_it: italianName(mat, "name"),
        image_url: mat.image_url,
        kind: "materials" as const,
      })),
    ];
    const pick = (pattern: RegExp, prefer: Entry["kind"]) => {
      const hits = entries.filter((e) => e.image_url && pattern.test(e.name));
      return hits.find((e) => e.kind === prefer) || hits[0];
    };
    const stages = [
      pick(/innocell|bacterial cellulose/i, "applications"), // Grow: cellulose grown in a bioreactor
      pick(/laser[- ]induced|carboni[sz]ed paper/i, "materials"), // Make: laser fabrication
      pick(/cnf.?hec|e-skin/i, "materials"), // Use: worn skin patch
      pick(/soluboard/i, "applications"), // Return: PCB substrate that dissolves in hot water
    ];
    return stages.map((e) => {
      if (!e) return null;
      const name = localizedName(locale, e.name, e.name_cn, e.name_it);
      return { src: e.image_url as string, caption: name.primary, lang: name.primaryLang, href: `/${e.kind}/${e.id}` };
    });
  }, [applications, materials, locale]);

  return (
    <div className="min-h-screen bg-background page-enter">
      <Helmet>
        <title>{`BioNexus — ${m.meta.siteTagline}`}</title>
        <meta name="description" content={m.meta.homeDescription} />
        <meta property="og:title" content={`BioNexus — ${m.meta.siteTagline}`} />
        <meta property="og:description" content={m.meta.homeDescription} />
        <meta property="og:type" content="website" />
      </Helmet>
      <SeoAlternates path="/" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24 px-6 lg:px-12">
        {/* A single, very faint wash behind the loop — no imagery */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute right-[-10%] top-[5%] h-[70%] w-[60%] bg-[radial-gradient(closest-side,hsl(var(--primary)/0.07),transparent)]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <h1
              className={
                locale === "zh"
                  ? "font-display font-medium leading-[1.2] text-[3.2rem] md:text-[4.4rem] lg:text-5xl"
                  : "font-display font-medium tracking-tight leading-[1.04] text-[3.6rem] md:text-5xl lg:text-6xl max-w-[17ch]"
              }
            >
              <SplitWords text={m.home.heroA} baseDelay={150} step={70} />{locale === "zh" ? null : " "}
              <span className="text-primary">
                <SplitWords text={m.home.heroB} baseDelay={360} step={70} />
              </span>{locale === "zh" ? <br /> : " "}
              <span className={locale === "zh" ? "font-light text-muted-foreground" : "font-light italic text-muted-foreground"}>
                <SplitWords text={m.home.heroC} baseDelay={450} step={70} />
              </span>
            </h1>
            <p className="hero-fade mt-10 max-w-[44rem] text-muted-foreground leading-relaxed" style={{ animationDelay: "750ms" }}>
              {m.home.heroLead}
            </p>
            <div className="hero-fade mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "850ms" }}>
              <Link
                to="/materials"
                className="group inline-flex items-center gap-3 rounded-full bg-foreground px-7 py-3.5 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
              >
                {m.home.exploreMaterials} <span className="arrow-nudge">→</span>
              </Link>
              <Link
                to="/applications"
                className="group inline-flex items-center gap-3 rounded-full px-5 py-3.5 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
              >
                {m.home.seeApplications} <span className="arrow-nudge">→</span>
              </Link>
            </div>

            {(materials?.length || applications?.length) ? (
              <dl className="hero-fade mt-16 flex flex-wrap gap-x-14 gap-y-6" style={{ animationDelay: "950ms" }}>
                {[
                  { label: m.home.statMaterials, value: materials?.length || 0 },
                  { label: m.home.statApplications, value: applications?.length || 0 },
                  { label: m.home.statSources, value: sourceCount },
                ].map((stat) => (
                  <div key={stat.label} className="flex flex-col-reverse">
                    <dt className="mt-2 text-sm text-muted-foreground">{stat.label}</dt>
                    <dd className="font-display text-[2.4rem] leading-none tabular-nums"><CountUp value={stat.value} /></dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>

          <div className="hero-fade lg:col-span-5 px-20 sm:px-24 lg:px-16 pt-6" style={{ animationDelay: "400ms" }}>
            <CircularityLoop images={loopImages} />
          </div>
        </div>
      </section>

      {/* Latest Applications */}
      <section className="pt-24 md:pt-30 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading title={m.home.latestApplications} action={<ViewAll to="/applications" />} />

          {applicationsLoading ? (
            <CardSkeletons ratio="aspect-video" />
          ) : latestApplications.length === 0 ? (
            <p className="text-muted-foreground">{m.home.noApplications}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestApplications.map((app, idx) => (
                <Reveal key={app.id} delay={idx * 80} className="h-full">
                  <ApplicationCard application={app} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Materials */}
      <section className="py-24 md:py-30 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading title={m.home.featuredMaterials} action={<ViewAll to="/materials" />} />

          {materialsLoading ? (
            <CardSkeletons ratio="aspect-[4/3]" />
          ) : featuredMaterials.length === 0 ? (
            <p className="text-muted-foreground">{m.home.noMaterials}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredMaterials.map((material, idx) => (
                <Reveal key={material.id} delay={idx * 80} className="h-full">
                  <MaterialCard material={material} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;